/**
 * video-reel.js — shoppable video rail plus an Instagram-style viewer.
 *
 * Loaded only by sections/video-reel.liquid, so pages without a video reel
 * never download it. Everything here is an enhancement: without JavaScript
 * each card is a plain link to its product, and the add-to-cart button is a
 * real product form that posts to /cart/add.
 */
(function () {
  'use strict';

  if (window.customElements.get('video-reel')) return;

  var reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Autoplaying video costs bandwidth the visitor may be paying for, so skip
   * it on metered or slow connections and when motion is unwelcome.
   */
  function autoplayAllowed() {
    if (reduceMotion) return false;

    var c = navigator.connection;
    if (c && (c.saveData || /^(slow-)?2g$/.test(c.effectiveType || ''))) return false;

    return true;
  }

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // How long a rail slide with nothing to play — a provider embed, or just a
  // cover image — holds the middle before the rail moves on.
  var DWELL = 6000;
  // How soon the rail asks again when it wanted to move on but the shopper
  // was mid-drag, or the viewer had taken over.
  var RETRY = 800;

  var DRAG_CLOSE_DISTANCE = 110; // a deliberate drag, at any speed
  var DRAG_CLOSE_VELOCITY = 0.55; // px per ms — a flick
  var DRAG_CLOSE_MIN = 40; // a flick still has to travel this far, so a
  //                          fast twitch cannot dismiss by accident

  class VideoReel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.track = this.querySelector('[data-reel-track]');
      this.template = this.querySelector('[data-reel-template]');
      this.viewer = this.querySelector('[data-viewer]');
      this.mayAutoplay = this.dataset.autoplay === 'true' && autoplayAllowed();
      this.externalInline = this.dataset.externalInline === 'true';

      this.strings = {
        add: this.dataset.tAdd || 'Add to cart',
        adding: this.dataset.tAdding || 'Adding…',
        added: this.dataset.tAdded || 'Added',
        error: this.dataset.tError || 'Could not add',
      };

      this.initRail();
      this.addEventListener('click', this.onCardClick.bind(this));
    }

    /* ------------------------------------------------------ the rail */

    /**
     * One video at a time, centre stage.
     *
     * The rail is a native snap scroller, so the browser decides what is
     * centred; this only reads that back. On each scrolled frame every slide
     * gets `--reel-d` — 0 dead centre, 1 out at the edge — which the CSS
     * turns into the coverflow, and whichever slide is nearest the middle
     * becomes the active one.
     *
     * Only the active slide ever plays. That is deliberate: a rail that
     * played everything on screen meant three or four videos decoding at once
     * for a shopper who can only watch one, and their competing silent motion
     * is what made the row read as busy rather than as a reel. When the
     * active video ends, the rail moves itself on to the next.
     */
    initRail() {
      if (!this.track) return;

      this.activeSlide = null;

      /**
       * A scroll-linked transform is motion, so where that is unwelcome the
       * focus becomes a plain two-step — centre slide full size, the rest
       * small — with no continuous scaling under the scroll.
       */
      this.steppedFocus = reduceMotion;

      var self = this;
      var frame = null;

      function schedule() {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          self.updateActive();
        });
      }

      this.track.addEventListener('scroll', schedule, { passive: true });

      /**
       * `ended` does not bubble, so this listens in the capture phase, which
       * every event passes through on its way down to its target. One
       * listener then covers every video in the rail, including the copies
       * the loop adds later.
       */
      this.track.addEventListener('ended', this.onEnded.bind(this), true);

      /**
       * theme.js copies the whole slide set either side to make the rail
       * endless. On a normal load that has already happened by the time this
       * element upgrades, but in the theme editor the element upgrades first
       * — so rather than cache a slide list, everything here reads
       * `track.children` live, and this re-measures when the copies land.
       */
      if (typeof MutationObserver === 'function') {
        this.railObserver = new MutationObserver(schedule);
        this.railObserver.observe(this.track, { childList: true });
      }

      if (typeof ResizeObserver === 'function') {
        this.railSize = new ResizeObserver(schedule);
        this.railSize.observe(this.track);
      } else {
        this.onResize = schedule;
        window.addEventListener('resize', schedule);
      }

      // A backgrounded tab should not keep decoding video.
      this.onVisibility = function () {
        if (document.hidden) self.pauseRail();
        else self.resumeRail();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      schedule();
    }

    /**
     * Picks the centred slide and hands every slide its distance from the
     * middle. This runs from a rAF and so may run on every scrolled frame:
     * the loops below only write a custom property, and the expensive part —
     * starting and stopping playback — is gated on the active slide actually
     * changing.
     */
    updateActive() {
      var slides = this.track.children;
      if (!slides.length) return;

      var box = this.track.getBoundingClientRect();
      var centre = box.left + box.width / 2;

      /**
       * Distances are measured in slides, not in pixels: one slide over is
       * always a full step back, whether that is 62vw on a phone or a 17rem
       * column on a wide desktop. Measuring against the track's half-width
       * instead made the step almost invisible on desktop, where four slides
       * fit and the neighbour is barely a third of the way out.
       *
       * `offsetLeft` rather than a rect, because the rects are what this
       * scales — a pitch read off them would shrink along with the slides.
       */
      var pitch =
        slides.length > 1 ? Math.abs(slides[1].offsetLeft - slides[0].offsetLeft) : 0;
      var reach = pitch || box.width / 2 || 1;

      var best = null;
      var bestDistance = Infinity;
      var spread = [];

      Array.prototype.forEach.call(slides, function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - centre);

        spread[i] = Math.min(1, distance / reach);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = slide;
        }
      });

      var stepped = this.steppedFocus;

      Array.prototype.forEach.call(slides, function (slide, i) {
        slide.style.setProperty(
          '--reel-d',
          stepped ? (slide === best ? '0' : '1') : spread[i].toFixed(3)
        );
      });

      if (best === this.activeSlide) return;

      if (this.activeSlide) {
        this.activeSlide.classList.remove('is-active');
        this.stopSlide(this.activeSlide);
      }

      this.activeSlide = best;
      best.classList.add('is-active');
      this.startSlide(best);
    }

    /**
     * Rolls the centred slide. A Shopify video plays and its `ended` moves
     * the rail on; anything else — a provider embed, or a slide that is only
     * a cover image — has no end to wait for, so it holds for a set beat
     * instead.
     */
    startSlide(slide) {
      clearTimeout(this.dwell);
      if (!this.mayAutoplay || document.hidden) return;

      var video = slide.querySelector('.reel__video');

      if (video) {
        var self = this;
        var attempt = video.play();

        if (attempt && attempt.then) {
          attempt
            .then(function () {
              video.classList.add('is-playing');
            })
            .catch(function () {
              // Autoplay refused, or the file would not load. The poster
              // stands in, and the rail moves on rather than stalling on a
              // frozen frame.
              self.queueAdvance(DWELL);
            });
        }
        return;
      }

      this.mountEmbed(slide);
      this.queueAdvance(DWELL);
    }

    /** Puts a slide back to rest as it leaves the middle. */
    stopSlide(slide) {
      clearTimeout(this.dwell);

      var video = slide.querySelector('.reel__video');
      if (video) {
        video.pause();
        video.classList.remove('is-playing');
      }

      // A provider iframe is well over a megabyte, so it is torn down the
      // moment its slide is no longer the one being watched.
      var slot = slide.querySelector('[data-reel-embed]');
      if (slot) slot.replaceChildren();
    }

    /**
     * Inline playback for YouTube and Vimeo, off by default. Even switched
     * on, a player is mounted only for the centred slide, so a rail of six
     * external videos never holds six iframes.
     */
    mountEmbed(slide) {
      if (!this.externalInline) return;

      var slot = slide.querySelector('[data-reel-embed]');
      if (!slot || slot.firstElementChild) return;

      var frame = document.createElement('iframe');
      frame.src = slot.getAttribute('data-reel-embed');
      frame.title = '';
      frame.tabIndex = -1;
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('allow', 'autoplay; encrypted-media');
      frame.setAttribute('loading', 'lazy');
      slot.appendChild(frame);
    }

    onEnded(event) {
      var video = event.target;
      if (!video.closest) return;

      var slide = video.closest('.reel__slide');
      if (!slide || slide !== this.activeSlide) return;

      video.classList.remove('is-playing');
      this.advance();
    }

    queueAdvance(delay) {
      clearTimeout(this.dwell);
      this.dwell = setTimeout(this.advance.bind(this), delay);
    }

    /**
     * Moves the rail on by one slide. The loop copies mean there is always a
     * next one, and theme.js quietly jumps the track back by a set once the
     * scroll settles, so this never runs out of runway.
     */
    advance() {
      clearTimeout(this.dwell);
      if (!this.activeSlide || !this.mayAutoplay) return;

      /**
       * Not while the shopper is dragging the rail, the tab is in the
       * background, or the full-screen viewer is up. Moving the track out
       * from under someone is worse than letting the sequence wait, so it
       * asks again in a moment instead.
       */
      var busy =
        document.hidden ||
        this.track.classList.contains('is-dragging') ||
        (this.viewer && !this.viewer.hidden);

      if (busy) {
        this.queueAdvance(RETRY);
        return;
      }

      var next = this.activeSlide.nextElementSibling;
      if (!next) return;

      var box = this.track.getBoundingClientRect();
      var rect = next.getBoundingClientRect();

      this.track.scrollTo({
        left: this.track.scrollLeft + (rect.left + rect.width / 2) - (box.left + box.width / 2),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    /** Everything quiet — a hidden tab, or the viewer taking over. */
    pauseRail() {
      clearTimeout(this.dwell);
      Array.prototype.forEach.call(this.querySelectorAll('.reel__video'), function (video) {
        video.pause();
        video.classList.remove('is-playing');
      });
    }

    /** Picks the sequence back up on whichever slide is centred. */
    resumeRail() {
      if (this.activeSlide) this.startSlide(this.activeSlide);
    }

    onCardClick(event) {
      var opener = event.target.closest('[data-reel-open]');
      if (!opener || !this.template) return;

      // Leave modified clicks alone: they mean "open the product elsewhere".
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      // Remember the element that was clicked, not document.activeElement:
      // Safari does not focus a link on click, so activeElement would be <body>
      // and focus would have nowhere sensible to return to on close.
      this.open(Number(opener.getAttribute('data-reel-open')) || 0, opener);
    }

    /* ---------------------------------------------------- the viewer */

    build() {
      if (this.built) return;
      this.built = true;

      // Escape any transformed ancestor so `position: fixed` means the viewport.
      document.body.appendChild(this.viewer);

      this.viewerTrack = this.viewer.querySelector('[data-viewer-track]');
      this.viewerTrack.appendChild(this.template.content.cloneNode(true));
      this.slides = Array.prototype.slice.call(
        this.viewerTrack.querySelectorAll('[data-viewer-slide]')
      );

      this.dots = this.viewer.querySelector('[data-viewer-dots]');
      this.slides.forEach(
        function () {
          var dot = document.createElement('span');
          dot.className = 'viewer__dot';
          this.dots.appendChild(dot);
        }.bind(this)
      );

      this.muted = true;
      this.index = 0;

      this.viewer.querySelector('[data-viewer-close]').addEventListener('click', this.close.bind(this));
      this.viewer.querySelector('[data-viewer-mute]').addEventListener('click', this.toggleMute.bind(this));
      this.viewerTrack.addEventListener('submit', this.onSubmit.bind(this));

      this.bindSwipe();
      this.bindDrag();
    }

    /** Native scroll-snap does the paging; this just tracks which slide won. */
    bindSwipe() {
      var self = this;

      this.slideObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.setActive(self.slides.indexOf(entry.target));
          });
        },
        { root: this.viewerTrack, threshold: 0.6 }
      );

      this.slides.forEach(function (slide) {
        self.slideObserver.observe(slide);
      });
    }

    /**
     * Drag down to dismiss. The track is `touch-action: pan-x`, so horizontal
     * gestures are handled natively by the scroller and vertical ones arrive
     * here untouched — no need to fight the browser for the gesture.
     */
    bindDrag() {
      var self = this;
      var start = null;
      var axis = null;
      var watchdog = null;

      function reset(animate) {
        clearTimeout(watchdog);
        watchdog = null;
        self.viewer.style.transition = animate ? '' : 'none';
        self.viewer.style.transform = '';
        self.viewer.style.opacity = '';
        start = null;
        axis = null;
      }

      /**
       * Last line of defence.
       *
       * Part way through a dismiss the viewer is a part-transparent black
       * sheet over the whole site. Every route out of the gesture is handled
       * below, but if any of them is ever missed the shopper is left looking
       * at the site through that sheet with no way back — measured at 37% on
       * a real device, over the header and everything else.
       *
       * So the drag is given a deadline rather than trusted to end. A real
       * one produces a steady stream of moves; going quiet for this long
       * means the gesture is gone, and the worst this can do to a slow
       * dragger is snap the sheet back to fully open.
       */
      function arm() {
        clearTimeout(watchdog);
        watchdog = setTimeout(function () {
          if (start) reset(true);
        }, 1200);
      }

      this.viewerTrack.addEventListener('pointerdown', function (event) {
        // Never steal a press aimed at a control.
        if (event.target.closest('a, button, select, input')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        start = { x: event.clientX, y: event.clientY, t: event.timeStamp, id: event.pointerId };
        axis = null;
        self.viewer.style.transition = 'none';
        arm();

        /**
         * Capture the pointer, so the rest of this gesture comes back here
         * whatever it ends up over.
         *
         * Without it a drag that finishes outside the track — the usual way a
         * dismiss gesture ends, since the whole point is to fling downwards —
         * never delivered `pointerup`, so nothing reset the inline styles set
         * below. The viewer stayed on the page: `position: fixed`, `inset: 0`,
         * `z-index: 100`, translated down and part-transparent over a black
         * background, which reads as a dark sheet across the whole site with
         * scrolling still locked.
         */
        try {
          this.setPointerCapture(event.pointerId);
        } catch (error) {
          /* Capture is an improvement, not a requirement. */
        }
      });

      this.viewerTrack.addEventListener('pointermove', function (event) {
        if (!start || event.pointerId !== start.id) return;

        var dx = event.clientX - start.x;
        var dy = event.clientY - start.y;

        if (axis === null) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          axis = Math.abs(dy) > Math.abs(dx) * 1.2 ? 'y' : 'x';
        }

        if (axis !== 'y' || dy <= 0) return;

        arm();
        self.viewer.style.transform = 'translateY(' + dy + 'px)';
        self.viewer.style.opacity = String(Math.max(0, 1 - dy / 420));
      });

      function end(event) {
        if (!start || event.pointerId !== start.id) return;

        var dy = event.clientY - start.y;
        var elapsed = Math.max(1, event.timeStamp - start.t);
        var velocity = dy / elapsed;
        var shouldClose =
          axis === 'y' &&
          (dy > DRAG_CLOSE_DISTANCE || (dy > DRAG_CLOSE_MIN && velocity > DRAG_CLOSE_VELOCITY));

        reset(true);
        if (shouldClose) self.close();
      }

      this.viewerTrack.addEventListener('pointerup', end);
      this.viewerTrack.addEventListener('pointercancel', function () {
        reset(true);
      });

      /**
       * The same two on the document, because pointer capture is not
       * guaranteed: it throws for a pointer the element never owned, and a
       * page that dispatches its own pointer events gets none of it.
       *
       * Ending the gesture must not depend on it. These fire wherever the
       * release lands, and re-entry is free — `end` and `reset` both clear
       * `start`, so the track's own listener running first simply makes
       * these no-ops.
       */
      document.addEventListener('pointerup', end);
      document.addEventListener('pointercancel', function () {
        if (start) reset(true);
      });

      /**
       * The last word on a gesture. Capture can be lost without either of the
       * two above firing — the browser taking over for its own scroll, the
       * element being re-rendered mid-drag — and every one of those routes
       * used to leave the viewer stuck over the page.
       */
      this.viewerTrack.addEventListener('lostpointercapture', function () {
        if (start) reset(true);
      });
    }

    open(index, opener) {
      this.build();

      /**
       * Start from a clean slate. If a dismiss gesture was ever lost part way
       * — an older build of this file, or a route nothing caught — the viewer
       * still carries that drag's inline `opacity` and `transform`, and
       * opening it again would bring the half-faded sheet straight back.
       */
      this.viewer.style.transition = '';
      this.viewer.style.transform = '';
      this.viewer.style.opacity = '';

      this.viewer.hidden = false;
      this.opener = opener || document.activeElement;

      // The rail is behind the viewer now, so nothing of it should still be
      // decoding — or, worse, scrolling itself on to the next slide.
      this.pauseRail();

      document.documentElement.style.overflow = 'hidden';
      this.setOutsideInert(true);

      // Jump to the tapped slide before the first paint so it never flashes
      // the wrong video.
      var slide = this.slides[index];
      if (slide) this.viewerTrack.scrollLeft = slide.offsetLeft;
      this.setActive(index);

      this.onKeydown = this.handleKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);

      this.viewer.focus({ preventScroll: true });
    }

    close() {
      this.viewer.hidden = true;
      this.stopAll();

      /**
       * Clear anything the dismiss drag left behind. Hiding alone is not
       * enough: a stale `opacity` or `transform` would still be on the
       * element the next time it opens, so it would come back part-faded and
       * pushed off-centre.
       */
      this.viewer.style.transition = '';
      this.viewer.style.transform = '';
      this.viewer.style.opacity = '';

      document.documentElement.style.overflow = '';
      this.setOutsideInert(false);
      document.removeEventListener('keydown', this.onKeydown);

      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });

      // The rail is the visible thing again, so the centred slide picks its
      // turn back up.
      this.resumeRail();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowRight') {
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        this.goTo(this.index - 1);
      }
    }

    goTo(index) {
      var slide = this.slides[index];
      if (!slide) return;

      this.viewerTrack.scrollTo({
        left: slide.offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    setOutsideInert(on) {
      var viewer = this.viewer;

      Array.prototype.forEach.call(document.body.children, function (child) {
        if (child === viewer) return;
        child.inert = on;
      });
    }

    /**
     * Exactly one slide's media is live at a time. For an external video that
     * also means its iframe `src` is only assigned while it is on screen, so
     * the provider is contacted once — for the video actually being watched —
     * rather than once per slide up front.
     */
    setActive(index) {
      if (index < 0 || (index === this.index && this.primed)) return;

      this.index = index;
      this.primed = true;

      var self = this;

      this.slides.forEach(function (slide, i) {
        var active = i === index;
        var video = slide.querySelector('[data-viewer-video]');
        var embed = slide.querySelector('[data-viewer-embed]');

        if (video) {
          video.muted = self.muted;
          if (active) {
            var attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(function () {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }

        if (embed) {
          var src = embed.getAttribute('data-viewer-embed');
          if (active) {
            var wanted = src + (self.muted ? '' : '&mute=0');
            if (embed.getAttribute('src') !== wanted) embed.setAttribute('src', wanted);
          } else if (embed.hasAttribute('src')) {
            // Removing src stops playback and releases the player entirely.
            embed.removeAttribute('src');
          }
        }
      });

      Array.prototype.forEach.call(this.dots.children, function (dot, i) {
        dot.setAttribute('aria-current', String(i === index));
      });
    }

    stopAll() {
      this.slides.forEach(function (slide) {
        var video = slide.querySelector('[data-viewer-video]');
        var embed = slide.querySelector('[data-viewer-embed]');
        if (video) video.pause();
        if (embed) embed.removeAttribute('src');
      });
      this.primed = false;
    }

    toggleMute() {
      this.muted = !this.muted;

      var button = this.viewer.querySelector('[data-viewer-mute]');
      button.setAttribute('aria-pressed', String(this.muted));

      // The label names the action, so it is the opposite of the current state.
      var label = button.querySelector('[data-viewer-mute-label]');
      if (label) label.textContent = this.muted ? button.dataset.tUnmute : button.dataset.tMute;

      this.primed = false;
      this.setActive(this.index);
    }

    /* ------------------------------------------------- add to cart */

    onSubmit(event) {
      var form = event.target.closest('form');
      if (!form) return;

      event.preventDefault();

      var button = form.querySelector('button[type="submit"]');
      var label = form.querySelector('[data-cta-label]') || button;
      var strings = this.strings;

      button.setAttribute('aria-disabled', 'true');
      label.textContent = strings.adding;

      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          label.textContent = strings.added;
          return fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (cart) updateCartCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        })
        .catch(function () {
          label.textContent = strings.error;
        })
        .then(function () {
          setTimeout(function () {
            label.textContent = strings.add;
            button.removeAttribute('aria-disabled');
          }, 2200);
        });
    }

    disconnectedCallback() {
      clearTimeout(this.dwell);
      if (this.railObserver) this.railObserver.disconnect();
      if (this.railSize) this.railSize.disconnect();
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.built && this.viewer && this.viewer.parentNode === document.body) {
        document.documentElement.style.overflow = '';
        this.setOutsideInert(false);
        this.viewer.remove();
      }
    }
  }

  /** Keeps the header bag badge honest after an Ajax add. */
  function updateCartCount(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  customElements.define('video-reel', VideoReel);
})();
